import { MarketplaceItemV2 } from '../../marketplace/marketplace-v2/models/marketplace-item-v2.model';
import { PortalProduct } from '../../portal-products/models/portal-product.model';
import { Cart } from '../models/cart.model';

/** Type guard — returns true if `object` is a `PortalProduct`, identified by the presence of `ProductId`. */
export function instanceOfPortalProduct(object: any): object is PortalProduct {
  return 'ProductId' in object;
}

/** Type guard — returns true if `object` is a `MarketplaceItemV2`, identified by the presence of `Id`. */
export function instanceOfMarketplaceItem(object: any): object is MarketplaceItemV2 {
  return 'Id' in object;
}

/**
 * Calculates the total price of a cart item based on its product type and purchase quantity.
 * - MarketplaceItemV2: `purchaseQuantity * MerchantValue`
 * - PortalProduct: `purchaseQuantity * MerchantPrice`
 * Returns 0 if the product type is unrecognised.
 */
export function calculateTotalPrice(cartItem: Cart): number {
  if (instanceOfMarketplaceItem(cartItem.productData)) {
    return cartItem.purchaseQuantity * cartItem.productData.MerchantValue;
  } else if (instanceOfPortalProduct(cartItem.productData)) {
    return cartItem.purchaseQuantity * cartItem.productData.MerchantPrice;
  }
  return 0;
}

/** Returns true if the cart item is an e-cash marketplace item (`EcashValue > 0`). */
export function isEcashItem(cartItem: Cart): boolean {
  return instanceOfMarketplaceItem(cartItem.productData) && cartItem.productData.EcashValue > 0;
}

/**
 * Returns true if the cart item is a points-only marketplace item.
 */
export function isPointsItem(cartItem: Cart): boolean {
  return (
    instanceOfMarketplaceItem(cartItem.productData) && cartItem.productData.EcashValue <= 0 && cartItem.productData.PointValue > 0
  );
}

/** Returns true if the cart item is a merchant-value marketplace item (`MerchantValue > 0`). */
export function isMerchantItem(cartItem: Cart): boolean {
  return instanceOfMarketplaceItem(cartItem.productData) && cartItem.productData.MerchantValue > 0;
}

/** Returns true if the cart item is a free marketplace item (no ecash, points, or merchant value). */
export function isFreeItem(cartItem: Cart): boolean {
  return (
    instanceOfMarketplaceItem(cartItem.productData) &&
    cartItem.productData.EcashValue <= 0 &&
    cartItem.productData.PointValue <= 0 &&
    cartItem.productData.MerchantValue <= 0
  );
}
